import { FC, HTMLAttributes, useEffect, useMemo } from "react";
import clsx from "classnames";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { featuredCardsAaaGql, Query, User } from "@onmo/graphql/graphql";
import { useStyles } from "./ConsoleMobileContent.style";
import { useAppSelector } from "../../hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import { ConsoleActivityCard } from "../ConsoleActivityCard/ConsoleActivityCard";
import { ConsoleRecentPlayCard } from "../ConsoleRecentPlayCard/ConsoleRecentPlayCard";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { GameCategory } from "../GameCategory";
import { ConsoleMobileCards } from "../ConsoleMobileCards";
import {
  ConsoleByogMobileGroup,
  ConsoleMobileId,
  FeatureMobileGroup,
  ConsolePidMobileGroup,
} from "../../../constants/console";
import {
  getFeatureCardsMap,
  getSubscriptionFeaturedCardId,
} from "../../../models/console";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { onMoveHomeCards } from "../../../models/gamepad";
import { useAAASubscription } from "../../../hooks/useAAASubscription";
import { useTranslation } from "react-i18next";
import {
  useCurrentProfile,
  useIsKidProfile,
} from "../../../hooks/useProfileSession";

interface IConsoleMobileContentProps {
  user?: User;
  totalTimePlayed: number;
  totalPlayedGames: number;
}

const ConsoleMobileContent: FC<
  HTMLAttributes<HTMLDivElement> & IConsoleMobileContentProps
> = (props) => {
  const classes = useStyles();
  const isKid = useIsKidProfile();
  const { currentProfile } = useCurrentProfile();
  const { t } = useTranslation();
  const { user, totalTimePlayed, totalPlayedGames, className, ...otherProps } =
    props;
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { setCallback, cleanup } = useGamepadContext();
  const { data: featuredCardsData } = useQuery<Query>(featuredCardsAaaGql, {
    variables: { input: { group: FeatureMobileGroup } },
  });
  const { data: byogFeaturedData } = useQuery<Query>(featuredCardsAaaGql, {
    variables: { input: { group: ConsoleByogMobileGroup } },
  });
  const { data: essentialData } = useQuery<Query>(featuredCardsAaaGql, {
    variables: { input: { group: ConsolePidMobileGroup } },
  });
  const { data: dataSubscription } = useAAASubscription({
    skip: !isAuthenticated,
  });

  const featureCards = useMemo(() => {
    const subscriptionFeaturedCardId = getSubscriptionFeaturedCardId(
      dataSubscription?.me?.subscription,
    );
    return getFeatureCardsMap(
      featuredCardsData,
      subscriptionFeaturedCardId.concat(ConsoleMobileId),
    );
  }, [dataSubscription?.me?.subscription, featuredCardsData]);

  const byogSection = useMemo(() => {
    const items = byogFeaturedData?.featuredCards?.items;
    if (!items?.length) return undefined;
    return {
      group: ConsoleByogMobileGroup,
      cards: items,
    } as Query["page"]["items"][number];
  }, [byogFeaturedData]);

  const pidSection = useMemo(() => {
    const items = essentialData?.featuredCards?.items;
    if (!items?.length) return undefined;
    return {
      group: ConsolePidMobileGroup,
      cards: items,
    } as Query["page"]["items"][number];
  }, [essentialData]);

  useEffect(() => {
    setCallback({
      onMoveAction: onMoveHomeCards,
    });

    return () => {
      cleanup();
    };
  }, [setCallback, cleanup]);

  return (
    <div {...otherProps} className={clsx(classes.root, clsx)}>
      <Typography
        variant={TypographyVariant.HeadingH5Bold}
        className={classes.username}
      >
        {user
          ? t("Console_Hello", {
              username: currentProfile?.name ?? user.username,
            })
          : t("Console_Welcome to Smart Console")}
      </Typography>
      {isAuthenticated && (
        <ConsoleActivityCard
          totalTimePlayed={totalTimePlayed}
          totalPlayedGames={totalPlayedGames}
        />
      )}
      {isAuthenticated && <ConsoleRecentPlayCard />}
      <ConsoleMobileCards featureCards={featureCards} />
      <GameCategory
        section={pidSection}
        defaultCardWidth={160}
        sessionTitle={t("Console_Essential Pass Games")}
        isHideRating
        sessionId="essential-pass"
      />
      {!isKid && (
        <GameCategory
          section={byogSection}
          defaultCardWidth={160}
          sessionTitle={t("Console_Install & Play Games")}
          isHideRating
          sessionId="install-play"
        />
      )}
    </div>
  );
};

export default ConsoleMobileContent;
